// Issue a new license. After success, the server returns key + secret which
// we show in a follow-up "Copy these now" dialog (visible exactly once).

import { useEffect, useState } from 'react';
import {
  Alert,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useCustomers } from '../customers/useCustomers';
import { useIssueLicense } from './useLicenses';

const { Text, Paragraph } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  customerId: string;
  plan: 'basic' | 'pro' | 'enterprise';
  features: string[];
  domain?: string | null;
  maxInstances?: number;
  expiresAt: Dayjs;
  price?: number;
  paymentStatus?: 'unpaid' | 'paid' | 'refunded';
  paymentReference?: string;
  notes?: string;
}

const FEATURE_OPTIONS = [
  { value: 'whiteLabel', label: 'White-label' },
  { value: 'multiUser', label: 'Multi-user' },
  { value: 'sftp', label: 'SFTP storage' },
  { value: 's3', label: 'S3 storage' },
];

export function IssueLicenseModal({ open, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const issue = useIssueLicense();
  const [issued, setIssued] = useState<{ key: string; secret: string } | null>(null);

  // Customer dropdown — load up to 100 since this is a single-tenant admin UI.
  const customersQuery = useCustomers({ page: 1, limit: 100, sort: 'name' });

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setIssued(null);
    }
  }, [open, form]);

  async function handleOk() {
    try {
      const values = await form.validateFields();
      const res = await issue.mutateAsync({
        customerId: values.customerId,
        plan: values.plan,
        features: values.features ?? [],
        domain: values.domain || null,
        maxInstances: values.maxInstances ?? 1,
        expiresAt: values.expiresAt.toISOString(),
        price: values.price ?? 0,
        paymentStatus: values.paymentStatus ?? 'unpaid',
        paymentReference: values.paymentReference ?? '',
        notes: values.notes ?? '',
      });
      message.success('Đã issue license');
      setIssued({ key: res.key, secret: res.secret });
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      if (apiMessage) message.error(apiMessage);
    }
  }

  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`Đã copy ${label}`);
    } catch {
      message.error('Trình duyệt không cho phép copy');
    }
  }

  // After issue, show the one-time reveal.
  if (issued) {
    return (
      <Modal
        open={open}
        title="License đã được tạo"
        onCancel={onClose}
        onOk={onClose}
        okText="Xong"
        cancelButtonProps={{ style: { display: 'none' } }}
        destroyOnClose
        width={620}
      >
        <Alert
          type="warning"
          showIcon
          message="Copy key + secret ngay. Secret sẽ KHÔNG hiển thị lại."
          style={{ marginBottom: 16 }}
        />
        <Paragraph>
          <Text strong>License key:</Text>
          <br />
          <Space>
            <Text code copyable={false} style={{ fontSize: 16 }}>{issued.key}</Text>
            <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => void copy(issued.key, 'key')}>
              <CopyOutlined /> Copy
            </Tag>
          </Space>
        </Paragraph>
        <Paragraph>
          <Text strong>HMAC secret:</Text>
          <br />
          <Space wrap>
            <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>{issued.secret}</Text>
            <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => void copy(issued.secret, 'secret')}>
              <CopyOutlined /> Copy
            </Tag>
          </Space>
        </Paragraph>
        <Paragraph type="secondary">
          Khách hàng chỉ cần nhập <Text code>key</Text> vào trang Activation của VR360 Tools.
          Secret sẽ được trao đổi tự động qua API activate.
        </Paragraph>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Issue license mới"
      onCancel={onClose}
      onOk={() => void handleOk()}
      okText="Issue"
      confirmLoading={issue.isPending}
      destroyOnClose
      width={620}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          plan: 'basic',
          maxInstances: 1,
          paymentStatus: 'unpaid',
          expiresAt: dayjs().add(1, 'year'),
        }}
      >
        <Form.Item name="customerId" label="Customer" rules={[{ required: true }]}>
          <Select
            placeholder="Chọn customer"
            loading={customersQuery.isLoading}
            options={(customersQuery.data?.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            showSearch
            optionFilterProp="label"
            // Render dropdown inside the modal — avoids AntD Modal focus-trap stealing
            // pointer events on portal'd dropdown options (the "see option but can't click" bug).
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </Form.Item>
        <Form.Item name="plan" label="Plan" rules={[{ required: true }]}>
          <Select
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            options={[
              { value: 'basic', label: 'Basic' },
              { value: 'pro', label: 'Pro' },
              { value: 'enterprise', label: 'Enterprise' },
            ]}
          />
        </Form.Item>
        <Form.Item name="features" label="Features">
          <Select
            mode="multiple"
            options={FEATURE_OPTIONS}
            placeholder="Chọn features (optional)"
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </Form.Item>
        <Form.Item name="domain" label="Domain lock (optional)" extra="Chỉ kích hoạt được trên domain này">
          <Input placeholder="tours.customer.com" allowClear />
        </Form.Item>
        <Form.Item name="maxInstances" label="Max instances">
          <InputNumber min={1} max={100} />
        </Form.Item>
        <Form.Item name="expiresAt" label="Expires at" rules={[{ required: true }]}>
          <DatePicker
            showTime
            style={{ width: '100%' }}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </Form.Item>
        <Form.Item name="price" label="Giá (VND)">
          <InputNumber min={0} style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
        </Form.Item>
        <Form.Item name="paymentStatus" label="Payment">
          <Select
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            options={[
              { value: 'unpaid', label: 'Unpaid' },
              { value: 'paid', label: 'Paid' },
              { value: 'refunded', label: 'Refunded' },
            ]}
          />
        </Form.Item>
        <Form.Item name="paymentReference" label="Mã hợp đồng">
          <Input placeholder="HD-2026-001" allowClear />
        </Form.Item>
        <Form.Item name="notes" label="Ghi chú">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
